import type { Language } from '../stores/displaySettings';
import { en } from './en';
import { es, type Catalogue } from './es';

/**
 * The active catalogue.
 *
 * Read-only by design. The preference itself belongs to `settings`, which
 * persists it and pushes it here through `applyDisplay` — the same path it uses
 * for reduced motion and the CRT layers. Keeping one owner is the point: a
 * language stored in two places is a language that eventually disagrees with
 * itself, and the settings screen would be the last to find out.
 *
 * Deliberately free of imports from the rest of the app so it can be unit
 * tested without dragging in audio, haptics or storage.
 */

const CATALOGUES: Record<Language, Catalogue> = { es, en };

class I18n {
  /** Mirrors `settings.display.language`; never assigned from anywhere else. */
  private current = $state<Language>('es');

  /**
   * The strings for the active language.
   *
   * A getter rather than a stored object so that reading `i18n.t` inside a
   * component subscribes it to the language: switching in Settings re-renders
   * every screen without a single explicit subscription.
   */
  get t(): Catalogue {
    return CATALOGUES[this.current];
  }

  get language(): Language {
    return this.current;
  }

  /** Called by `applyDisplay`. Not for components. */
  setLanguage(language: Language): void {
    this.current = language;
  }
}

export const i18n = new I18n();

export { en, es, type Catalogue };
